import React, { useState } from "react";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useToast } from "../../components/toast";
import { EditableField } from "../../components/editable-field";
import { ErrorMessage } from "../../components/error-message";
import { Button } from "../../components/button";
import { PHONE_NUMBER_ARGENTINA_PATTERN } from "../../utils/patterns";
import { DateField } from "../../components/date-field";
import { CreateHelpDeskTicket } from "../../types/help_desk_tickets.types";
import { HelpDeskTicketsAPI } from "../../APIs/help_desk_tickets.api";

type ForgotCredentialsFormProps = {
  route?: {
    pathname?: string;
    search?: string;
  };
  onSubmitSuccess?: (ticketData: CreateHelpDeskTicket) => void;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";

  const parts = dateString.split("-");
  if (parts.length !== 3) return dateString;

  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

export const ForgotCredentialsForm = ({
  route = {},
  onSubmitSuccess,
}: ForgotCredentialsFormProps) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    dni: "",
    phone_number: "",
    birth_date: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();
  const { handleError, error, clearError } = useErrorHandler();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    clearError();

    try {
      const title = "Recuperación de acceso – Usuario y contraseña olvidados";

      const description = `
DNI: ${formData.dni}
Nombre: ${formData.first_name}
Apellido: ${formData.last_name}
Fecha de Nacimiento: ${formatDate(formData.birth_date)}
Número de Teléfono: ${formData.phone_number}
      `.trim();

      const ticketData: CreateHelpDeskTicket = {
        title,
        description,
        dni: formData.dni,
      };

      const response = await HelpDeskTicketsAPI.createHelpDeskTicket(
        ticketData,
        false
      );

      if (response.statusCode === 201) {
        addToast({
          message: "Solicitud enviada",
          type: "success",
          location: "bottom-center",
        });
        if (onSubmitSuccess) {
          onSubmitSuccess(ticketData);
        }
      } else {
        handleError(response.message, { persist: true });
      }
    } catch (error) {
      handleError("Error inesperado. Intente nuevamente.", { persist: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    if (error) clearError();
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <EditableField
          type="text"
          label="Nombre"
          name="first_name"
          id="first_name"
          required
          value={formData.first_name}
          onChange={handleChange}
          minLength={3}
          maxLength={100}
          placeholder="Ingresa tu nombre"
        />

        <EditableField
          type="text"
          label="Apellido"
          name="last_name"
          id="last_name"
          required
          value={formData.last_name}
          onChange={handleChange}
          minLength={3}
          maxLength={100}
          placeholder="Ingresa tu apellido"
        />

        <EditableField
          type="text"
          label="DNI"
          name="dni"
          id="dni"
          required
          value={formData.dni}
          onChange={handleChange}
          pattern="^\d{7,8}$"
          title="El DNI debe tener entre 7 y 9 dígitos."
          minLength={7}
          maxLength={9}
          placeholder="Ingresa tu DNI sin puntos"
        />

        <EditableField
          type="text"
          label="Número de Teléfono"
          name="phone_number"
          id="phone_number"
          required
          value={formData.phone_number}
          onChange={handleChange}
          placeholder="Ingresa tu número de teléfono"
          pattern={PHONE_NUMBER_ARGENTINA_PATTERN}
          title="Ingrese un número de teléfono válido (ejemplo: 3777112233)."
          minLength={10}
          maxLength={10}
        />

        <DateField
          label="Fecha de Nacimiento"
          type="date"
          value={
            formData.birth_date
              ? new Date(formData.birth_date).toISOString().split("T")[0]
              : ""
          }
          onChange={handleChange}
          name="birth_date"
          id="birth_date"
          required
        />

        {error && (
          <ErrorMessage message={error} dismissible onDismiss={clearError} />
        )}
        <Button
          type="submit"
          disabled={isSubmitting || error !== null}
          text="Enviar Solicitud"
          variant="auth"
        />
      </form>
    </>
  );
};
